import React, { FC, useEffect, useRef, useState } from "react";
import "./TableFilters.scss";
import { useTranslation } from "react-i18next";
import TextInputWithIcon from "../../TextInputWithIcon";
import { svgs } from "../../../common/assats";
import Button from "../../Button";
import { useOutsideAlerter } from "../../../common/hooks/UseClickOutside";
import { GenericRecord } from "../../../common/types/filterObjType";
import RangeDatePicker from "./DatePicker";
import { DateRange } from "react-day-picker";
import ClickableSVG from "../../ClickableSVG/ClickableSVG";
import FilterChip from "./FilterChip";
import { capitalizeFirstLetter } from "../../../common/function/capitalizeFirstLetter";

const i18nPrefix = "table.filter";

export interface TableFiltersProps {
  tableHeadline?: string;
  trashAction: () => void;
  filters?: string[];
  setFilters: ([]) => void;
  searchValue?: string;
  setSearchValue?: (val: string) => void;
  searchPlaceholder?: string;
  buttonText?: string;
  subButtonText?: string;
  buttonAction?: () => void;
  subAction?: () => void;
  filterObj?: GenericRecord<string>;
  dateFilter?: {
    selectedRange: DateRange | undefined;
    setSelectedRange: (val: DateRange | undefined) => void;
  };
  showFilterChips?: boolean;
  autoFocus?: boolean;
}

const TableFilters: FC<TableFiltersProps> = ({
  tableHeadline,
  trashAction,
  filters,
  setFilters,
  searchValue,
  setSearchValue,
  searchPlaceholder,
  buttonText,
  buttonAction,
  subButtonText,
  subAction,
  filterObj,
  dateFilter,
  showFilterChips = false,
  autoFocus,
}) => {
  const { t } = useTranslation();
  const getText = (text: string) => t(`${i18nPrefix}.${text}`);
  const [openFilters, setOpenFilters] = useState(false);
  const [rowFilters, setRowFilters] = useState<string[]>(filters || []);
  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef, () => {
    setOpenFilters(false);
  });
  useEffect(() => {
    if (openFilters && filters) {
      setRowFilters([...filters]); // Load applied filters into rowFilters
    }
  }, [openFilters, filters]);

  function addOrRemoveFilter(filter: string) {
    if (!filter) return;

    setRowFilters(
      (prevFilters) =>
        prevFilters.includes(filter)
          ? prevFilters.filter((f) => f !== filter) // Remove filter
          : [...prevFilters, filter] // Add filter
    );
  }
  const applyFilters = () => {
    setFilters([...rowFilters]); // Save filters globally
    setOpenFilters(false); // Close dropdown
  };
  return (
    <div className="table-filters-main-container">
      <div className="filters-wrapper">
        {searchValue !== undefined && setSearchValue && (
          <div className="search-input">
            <TextInputWithIcon
              autoFocus={autoFocus}
              Icon={svgs.logos.Search}
              placeholder={searchPlaceholder || ""}
              setValue={setSearchValue}
              value={searchValue}
              inputName="email"
              extraStyles={{ height: "2.5rem" }}
            />
          </div>
        )}
        {filters !== undefined && (
          <div className="flex-center filter-button" ref={wrapperRef}>
            <div
              className="content-wrapper"
              onClick={() => {
                setOpenFilters(!openFilters);
              }}
            >
              {" "}
              {/* Use Fragment to avoid additional div */}
              <div className="icon-container flex-center ">
                <svgs.logos.FiltersIcon className="filterIcon" />
              </div>
              <span className="filter-text">{getText(`filter`)}</span>
            </div>

            {/* Filters Dropdown */}
            {openFilters && (
              <div className="filterDropdown">
                {filterObj &&
                  (Object.keys(filterObj) as Array<keyof typeof filterObj>).map(
                    (key) => (
                      <div key={key} className="filter-group">
                        <span className="filter-headline">
                          {capitalizeFirstLetter(key)}
                        </span>
                        {filterObj[key].map((val, idx) => (
                          <div
                            onClick={() => {
                              addOrRemoveFilter(val.value);
                            }}
                            key={idx}
                            className="filter-unit"
                          >
                            <span>{capitalizeFirstLetter(val.name)}</span>
                            {rowFilters.some((ind) => ind == val.value) ? (
                              <svgs.icons.RadioButtonOn />
                            ) : (
                              <svgs.icons.RadioButtonOff />
                            )}
                          </div>
                        ))}
                      </div>
                    )
                  )}
                <div className="apply" onClick={applyFilters}>
                  <span>Apply</span>
                </div>
              </div>
            )}
          </div>
        )}
        {dateFilter && (
          <RangeDatePicker
            selectedRange={dateFilter.selectedRange}
            setSelectedRange={dateFilter.setSelectedRange}
          />
        )}
        {buttonText && buttonAction && (
          <div className="add-button-container">
            <Button
              extraStyles={{ height: "2.5rem" }}
              Icon={svgs.logos.Plus}
              onClick={buttonAction}
              text={buttonText}
            />
          </div>
        )}
        {subButtonText && subAction && (
          <ClickableSVG
            Icon={svgs.icons.exportXlsx}
            buttonAction={subAction}
            marginLeft={Boolean(dateFilter)}
          />
        )}

        {showFilterChips && filters && filters.length > 0 && (
          <div className="selected-filters">
            {filters.map((filterValue) => (
              <FilterChip
                key={filterValue}
                label={filterValue}
                onRemove={() => {
                  // remove just this one from the filters array
                  setFilters(filters.filter((f) => f !== filterValue));
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TableFilters;
